import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/auth'
const API_BASE = import.meta.env.VITE_API_BASE

/**
 * Authentication service for managing user sessions.
 *
 * Handles login, logout, and session validation with the API.
 */
class SessionService {
    /**
     * Create a new user session (login).
     *
     * @param {Object} user - User credentials
     * @param {string} user.email - User email address
     * @param {string} user.password - User password
     * @returns {Promise<Object>} Session data
     * @throws {string} Error message if authentication fails
     */
    async store(user) {
        await apiClient.get(API_BASE + '/sanctum/csrf-cookie')

        const response = await apiClient.post(API_INTERNAL_BASE, {
            email: user.email,
            password: user.password,
            device_name: 'app-web',
        })

        return response.data
    }

    /**
     * Destroy the current user session (logout).
     *
     * @returns {Promise<void>} Resolves when logout is complete
     * @throws {string} Error message if logout fails
     */
    async destroy() {
        return await apiClient.delete(API_INTERNAL_BASE)
    }

    /**
     * Retrieve the current authenticated session payload.
     *
     * @returns {Promise<Object>} Session data
     */
    async session() {
        const response = await apiClient.get(API_INTERNAL_BASE)
        return response.data
    }

    /**
     * Validate the current session is still active.
     *
     * @returns {Promise<boolean>} True if session is valid, false otherwise
     */
    async validate() {
        return await apiClient
            .get(API_INTERNAL_BASE)
            .then(() => true)
            .catch(() => false)
    }
}

export default new SessionService()
